import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

const CONTENT = {
  about: {
    title: "About GTForce",
    intro: "GTForce — Global Task Force — is a workforce operating system built to run distributed field, sales and task operations at planetary scale.",
    sections: [
      { h: "Our mission", p: "Give every worker a place where their tasks, earnings and achievements are visible — and every organization a single command center for the global workforce behind them." },
      { h: "The GTForce ecosystem", p: "AI World University trains workers → AI Gig Jobs helps them discover work → GTForce is where they execute field, sales and operational work → client businesses receive leads, appointments and sales → revenue and incentives are tracked end to end." },
      { h: "Built for operators", p: "Fast for workers on their phones. Data-rich for managers. Powerful for administrators running a global command center." },
    ],
    bullets: ["Global org hierarchy", "Role-based access control", "Timezone-aware operations", "Privacy-first location handling"],
  },
  "how-it-works": {
    title: "How It Works",
    intro: "From assignment to approval to revenue — a clean operational loop for every worker, everywhere.",
    sections: [
      { h: "1. Assign", p: "Admins create campaigns and tasks and assign them to workers, teams and cities with clear targets and KPIs." },
      { h: "2. Execute", p: "Workers start tasks on mobile, record calls, visits, leads and meetings, and submit evidence for review." },
      { h: "3. Verify", p: "Managers review submissions and approve or reject. Approved work feeds performance and earnings automatically." },
      { h: "4. Report", p: "Performance rolls up from worker to team to city to country to global — in real time, in your reporting currency." },
    ],
    bullets: ["Task lifecycle with evidence", "Multi-currency sales tracking", "Automated earnings & incentives", "AI-assisted daily summaries"],
  },
  "for-workers": {
    title: "For Workers",
    intro: "A field app designed for your thumb. See your target, do the work, watch your earnings grow.",
    sections: [
      { h: "Your day at a glance", p: "A big progress ring shows today's target vs completed. Quick actions let you add a lead, start a task or record a visit in seconds." },
      { h: "Get paid for results", p: "Task approvals, leads, meetings and sale commissions all convert to earnings you can track — pending, approved and paid." },
      { h: "Grow your skills", p: "Training and certifications keep you moving up, with future links into AI World University." },
    ],
    bullets: ["Mobile-first & installable", "Minimal typing", "Live earnings", "Personal milestones"],
  },
  "for-businesses": {
    title: "For Businesses",
    intro: "Deploy a managed global task force for field sales, lead generation and operational work — with full visibility.",
    sections: [
      { h: "Run campaigns anywhere", p: "Launch campaigns across countries and cities with target vs actual dashboards for leads, meetings, sales and revenue." },
      { h: "Total transparency", p: "See exactly what every team and city is producing, spot who's falling behind, and act with configurable alerts." },
      { h: "Scale without redesign", p: "Architected to grow from hundreds to millions of workers with pagination, indexing and efficient queries." },
    ],
    bullets: ["Campaign target tracking", "City & country performance", "Configurable KPIs & alerts", "Exportable reports"],
  },
  opportunities: {
    title: "Opportunities",
    intro: "Join a global network of field workers, sales agents and operators. Work flows to you.",
    sections: [
      { h: "Field sales agents", p: "Generate leads, book meetings and close deals with clear commissions and incentives." },
      { h: "Field operations", p: "Complete verified field visits and operational tasks for client businesses in your city." },
      { h: "Team leaders", p: "Lead a team, review submissions and drive your city's performance up the board." },
    ],
    bullets: ["Flexible field work", "Performance-based earnings", "Clear daily targets", "Growth pathways"],
  },
  faq: {
    title: "Frequently Asked Questions",
    intro: "Everything you need to know about the Global Task Force.",
    faqs: [
      { q: "How do I join as a worker?", a: "Click Get Started and register. You'll receive a unique Worker ID and land in your mobile dashboard." },
      { q: "How are earnings calculated?", a: "Earnings come from approved tasks, leads created, meetings booked and commission on closed sales, per your organization's configured rules." },
      { q: "Is my location tracked?", a: "No. GTForce never tracks location continuously. Location is only collected when a specific field task explicitly requires verification, and it's clearly communicated." },
      { q: "What currencies are supported?", a: "Sales can be recorded in multiple currencies and are normalized to a configurable reporting currency (default INR)." },
      { q: "Can it scale?", a: "Yes — GTForce grows with your people, whether you're managing a single city team or a global force of millions of workers." },
    ],
  },
};

export function InfoPage({ slug }) {
  const data = CONTENT[slug];
  if (!data) return null;
  return (
    <div>
      <section className="bg-slate-50/60 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-5 py-16">
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0A1128]">{data.title}</h1>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl">{data.intro}</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-5 py-14">
        {data.sections && (
          <div className="grid md:grid-cols-2 gap-8">
            {data.sections.map((s) => (
              <div key={s.h}>
                <h3 className="font-head text-xl font-semibold text-[#0A1128]">{s.h}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        )}
        {data.bullets && (
          <div className="mt-12 grid sm:grid-cols-2 gap-3">
            {data.bullets.map((b) => (
              <div key={b} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white">
                <CheckCircle2 className="text-[#1D4ED8]" size={20} /> <span className="font-medium text-[#0A1128]">{b}</span>
              </div>
            ))}
          </div>
        )}
        {data.faqs && (
          <div className="space-y-4">
            {data.faqs.map((f) => (
              <div key={f.q} className="p-6 rounded-2xl border border-slate-200 bg-white">
                <h3 className="font-head text-lg font-semibold text-[#0A1128]">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-14 text-center">
          <Link to="/register" className="px-7 py-3 rounded-full bg-[#1D4ED8] text-white font-semibold hover:bg-[#1E40AF] transition-colors shadow-lg shadow-orange-500/30">Get Started</Link>
        </div>
      </section>
    </div>
  );
}
